import { serviceApi, withServiceRequestDefaults } from 'Shared/network';
import dayjs from 'dayjs';
import type { GetPostViewingPenaltyInfoResponseDto } from 'viewup-api/v2';
import { PostViewingRecordFailReason } from 'viewup-api/v2';
import { PostCampaignStatus } from '../../model/types';

export type isSuspendedResult = {
  accountName: string;
  isSuspended: true;
  penaltyCount: number;
  penaltyCountMap: Record<PostCampaignStatus.NOT_SUBMITTED | PostCampaignStatus.REJECT, number>;
  penaltyPoint: number;
  suspensionEndDate?: Date;
};
export type isNotSuspendedResult = {
  accountName: string;
  isSuspended: false;
  penaltyCount: number;
};

export type GetPenaltyInfoReq = {
  accountUuid: string;
};
export type GetPenaltyInfoRes = isSuspendedResult | isNotSuspendedResult;

export const getPenaltyInfo = async (params: GetPenaltyInfoReq): Promise<GetPenaltyInfoRes> => {
  const { accountUuid } = params;
  const { data: response } = await serviceApi.viewing.getPostViewingPenaltyInfo(
    withServiceRequestDefaults({
      snsAccountUuid: accountUuid,
    }),
  );

  return dtoToDomain(response.data);
};

export const isSuspendedPredicate = (response: GetPenaltyInfoRes): response is isSuspendedResult =>
  response.isSuspended === true;

const dtoToDomain = (data: GetPostViewingPenaltyInfoResponseDto): GetPenaltyInfoRes => {
  const { isSuspended, penaltyCount, penaltyPoint, penaltyReasons, snsId, suspensionEndDate } =
    data;

  if (isSuspended) {
    return {
      accountName: snsId,
      isSuspended: true,
      penaltyCount: penaltyReasons?.length || 0,
      penaltyCountMap: createPenaltyCountMap(penaltyReasons || []),
      penaltyPoint: penaltyPoint || 0,
      suspensionEndDate: dayjs(suspensionEndDate).toDate(),
    };
  }

  return {
    accountName: data.snsId,
    isSuspended: false,
    penaltyCount: penaltyCount || 0,
  };
};

const createPenaltyCountMap = (
  penaltyReasons: PostViewingRecordFailReason[],
): isSuspendedResult['penaltyCountMap'] => {
  const initialCountMap: isSuspendedResult['penaltyCountMap'] = {
    [PostCampaignStatus.REJECT]: 0,
    [PostCampaignStatus.NOT_SUBMITTED]: 0,
  };

  return penaltyReasons.reduce((acc, reason) => {
    if (reason === PostViewingRecordFailReason.Rejected) {
      acc[PostCampaignStatus.REJECT] += 1;
    } else {
      acc[PostCampaignStatus.NOT_SUBMITTED] += 1;
    }
    return acc;
  }, initialCountMap);
};
